'use client';
import { Calculate, RestartAlt } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';

import { trackEvent } from '@/lib/analytics/gtm';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksCalculatorGrid,
  Enum_Componentshareddisplayoptions_Contentswidth,
  FinancialCalculator as FinancialCalculatorType,
} from '@/types.d';

import { CustomBackground } from '../CustomBackground/CustomBackground';

const evaluateFormula = (formula: string, values: Record<string, number>) => {
  try {
    const expr = formula.replace(/\b[A-Za-z_]\w*\b/g, (match) => String(values[match] ?? 0));
    const result = eval(expr);

    // Return null if result is not finite (division by zero)
    if (!isFinite(result)) return null;

    return isNaN(result) ? null : result;
  } catch {
    return null;
  }
};

const Calc = ({
  calc,
  index,
  Column,
}: {
  calc: FinancialCalculatorType;
  index: number;
  Column: number | undefined;
}) => {
  const fieldKeys = Object.keys(calc.input_fields ?? {});
  const { register, watch, reset } = useForm<Record<string, string>>({
    defaultValues: Object.fromEntries(fieldKeys.map((k) => [k, ''])),
  });
  const trackDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rawValues = watch();
  const result = evaluateFormula(
    calc.formula ?? '',
    Object.fromEntries(Object.entries(rawValues).map(([k, v]) => [k, parseFloat(v) || 0]))
  );

  if (!calc) return null;

  const handleChange = (field: string) => {
    if (trackDebounce.current) clearTimeout(trackDebounce.current);
    trackDebounce.current = setTimeout(() => {
      trackEvent({
        feature: 'calculator',
        action: 'calculator_interaction',
        params: { calculator_type: calc.name, field_name: field },
      });
    }, 600);
  };

  return (
    <Grid item xs={12} sm={6} md={12 / (Column ?? 1)} key={index}>
      <Card
        variant="outlined"
        sx={(theme) => ({
          p: 0,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.common.white,
          transition: '0.2s',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderColor: theme.palette.primary.main,
          },
        })}
      >
        <CardContent
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          {/* Header */}
          <Box display="flex" alignItems="center" mb={1}>
            <Calculate sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ flexGrow: 1 }}>
              {calc.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => reset()}
              aria-label="Reset calculator"
              sx={{ ml: 0.5 }}
            >
              <RestartAlt fontSize="small" />
            </IconButton>
          </Box>

          {/* Formula */}
          <Typography
            variant="caption"
            sx={{
              background: '#f9fafb',
              borderRadius: 1,
              p: 1,
              mb: 1.5,
              border: '1px solid #e5e7eb',
              fontStyle: 'italic',
              display: 'block',
            }}
          >
            {calc.formula}
          </Typography>

          {/* Inputs Scrollable */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 1 }}>
            {fieldKeys.map((field) => (
              <TextField
                key={field}
                fullWidth
                margin="dense"
                size="small"
                label={field}
                type="number"
                {...register(field, {
                  onChange: () => handleChange(field),
                })}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  mb: 1,
                }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          {/* Result */}
          <Box
            textAlign="center"
            p={1.5}
            border="2px dashed #3b82f6"
            borderRadius={2}
            sx={{ backgroundColor: '#f0f9ff' }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {calc.name}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {result !== null ? result.toFixed(2) : '--'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const FinancialCalculator = ({ block }: { block: ComponentBlocksCalculatorGrid }) => {
  const { CalculatorTitle, Rows, Column, Calculators } = block;

  const sortedCalculators = [...Calculators].sort(
    (a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0)
  );

  const maxItems = (Rows ?? 1) * (Column ?? 1);
  const visibleCalculators = sortedCalculators.slice(0, maxItems);

  return (
    <CustomBackground
      backgroundOptions={(block?.CalculatorGridBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...block?.CalculatorGridDisplayOptions,
          ContentsWidth:
            block?.CalculatorGridDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: block?.CalculatorGridDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: block?.CalculatorGridDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: block?.CalculatorGridDisplayOptions?.PaddingInline ?? 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error actually does accept data-* prop
        'data-component': 'FinancialCalculator',
        sx: { py: 3 },
      }}
    >
      <Container maxWidth="lg">
        {/* Title */}
        <Typography variant="h2" fontWeight="bold" textAlign="center" gutterBottom>
          {CalculatorTitle}
        </Typography>

        {/* Calculators Grid */}
        <Grid container spacing={2}>
          {visibleCalculators.map((calc: unknown, index: number) => (
            <Calc
              key={`${Column}${index}`}
              {...{
                calc: calc as FinancialCalculatorType,
                Column: (Column || undefined) as number | undefined,
                index,
              }}
            />
          ))}
        </Grid>
      </Container>
    </CustomBackground>
  );
};

export default FinancialCalculator;
